import React, { createContext, useContext } from "react";
import { useQueryClient } from '@tanstack/react-query';
import { useLogout, useAuthState } from '@/features/auth/hooks/useAuth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const logoutMutation = useLogout();
  const { data: authState, isLoading } = useAuthState();

  const login = ({ token, role, userId }) => {
    // For backward compatibility, set directly in localStorage
    // This supports the existing login() API that sets tokens directly
    // (e.g., from LoginComponent after successful API login)
    localStorage.setItem("authToken", token);
    localStorage.setItem("userRole", role);
    localStorage.setItem("userId", userId);
    
    // Update query cache to trigger reactive updates
    queryClient.setQueryData(['auth'], { token, userId, role });
  };

  const logout = async () => {
    if (authState?.userId) {
      await logoutMutation.mutateAsync(authState.userId);
    } else {
      // Fallback: clear localStorage if no userId
      localStorage.removeItem("authToken");
      localStorage.removeItem("userRole");
      localStorage.removeItem("userId");
      window.location.href = '/';
    }
  };

  const authToken = authState?.token || null;
  const userRole = authState?.role || "";
  const userId = authState?.userId || "";

  return (
    <AuthContext.Provider
      value={{ authToken, userRole, userId, isLoading, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  const { authToken, userRole, userId, isLoading, login, logout } = context;

  const isAuthenticated = !!authToken && !!userId;

  return {
    authToken,
    userRole,
    userId,
    isAuthenticated,
    isLoading,
    login,
    logout,
  };
};
